import { useNavigate, useSearchParams } from 'react-router-dom';

import {
    Body1,
    FilledCard,
    Heading1,
    Stack,
    Subtitle1,
} from '@myphx-engineering/phx-design-system';

import type {
    PathwayQuestionnaireConfig,
    QuestionnaireOption,
} from '../../../configuration/shared';
import { useFillPrescription } from '../../../contexts/fill/use-fill-prescription';
import { getAbsoluteRoute } from '../../../util/get-absolute-route';
import { ConditionalComponent } from '../../utility/ConditionalComponent';

import { UnsureCoverageLinkModal } from './common/UnsureCoverageLinkModal';

interface Props {
    questionnaire: PathwayQuestionnaireConfig;
    testIdPrefix: string;
}

export const CoverageQuestionnaire = ({
    questionnaire,
    testIdPrefix,
}: Props) => {
    const navigate = useNavigate();
    const { drxId } = useFillPrescription();
    const [searchParams] = useSearchParams();

    const handleOptionSelect = (option: QuestionnaireOption) => {
        if (option.subQuestionnaire) {
            navigate(
                {
                    pathname: getAbsoluteRoute('fill.drxId.coverageSubSelect', {
                        drxId,
                    }),
                    search: searchParams.toString(),
                },
                { state: { subQuestionnaire: option.subQuestionnaire } }
            );
            return;
        }

        if (option.target) {
            const params = new URLSearchParams(searchParams.toString());
            params.set('pathway', option.target.pathway);
            params.set('original-pathway', 'fallback');
            if (option.target.subPathway) {
                params.set('sub-pathway', option.target.subPathway);
            }

            const screen = option.target.destination ?? 'path-select';
            const routeKey =
                screen === 'attestation'
                    ? 'fill.drxId.root'
                    : screen === 'pharmacy-select'
                      ? 'fill.drxId.pharmacy.root'
                      : 'fill.drxId.pathSelect';
            navigate({
                pathname: getAbsoluteRoute(routeKey, { drxId }),
                search: `?${params.toString()}`,
            });
        }
    };

    return (
        <Stack gap="md">
            <Stack gap="xs" data-testid={`${testIdPrefix}-body-copy`}>
                <Heading1>{questionnaire.heading}</Heading1>
                <ConditionalComponent condition={!!questionnaire.description}>
                    <Body1>{questionnaire.description}</Body1>
                </ConditionalComponent>
            </Stack>
            <Stack gap="xs">
                {questionnaire.options.map((option) => (
                    <FilledCard
                        key={option.id}
                        header={<Subtitle1>{option.content.heading}</Subtitle1>}
                        onClick={() => handleOptionSelect(option)}
                        data-testid={`${testIdPrefix}-option-${option.id}`}
                    >
                        <ConditionalComponent
                            condition={!!option.content.description}
                        >
                            <Body1>{option.content.description}</Body1>
                        </ConditionalComponent>
                    </FilledCard>
                ))}
            </Stack>
            {questionnaire.notFoundText && questionnaire.notFoundModal && (
                <UnsureCoverageLinkModal
                    linkText={questionnaire.notFoundText}
                    modalHeading={questionnaire.notFoundModal.heading}
                    modalBody={questionnaire.notFoundModal.body}
                    modalLinkUrl={questionnaire.notFoundModal.linkUrl}
                    testIdPrefix={testIdPrefix}
                />
            )}
        </Stack>
    );
};
